import React from 'react'
import classNames from 'classnames'

import { mq } from 'marketing-site/src/library/utils'
import { Text } from 'marketing-site/src/library/elements/Text'
import { RichText } from 'marketing-site/src/library/elements/RichText'
import { IProps } from './index'
import kebabCase from 'lodash.kebabcase'

export const LargeTestimonial: React.FC<IProps> = ({
  layout = 'media-right',
  image,
  eyebrow,
  quote,
  authorName,
  authorRole,
  companyLogo,
  cta,
  backgroundColor = 'transparent',
}) => {
  const classes = classNames('large-testimonial', 'grid-layout', {
    [`large-testimonial--${layout}`]: layout,
    [`mvp-background-color-${kebabCase(backgroundColor)}`]: backgroundColor,
  })

  return (
    <div className={classes} data-testid="large-testimonial">
      <div className="large-testimonial__inner">
        <div className="large-testimonial__image">{image}</div>
        <div className="large-testimonial__header">
          {eyebrow && (
            <div className="large-testimonial__eyebrow">
              <Text size="eyebrow">{eyebrow}</Text>
            </div>
          )}
          <blockquote className="large-testimonial__quote">
            <Text size="subhead-lg">
              <RichText html={quote} />
            </Text>
          </blockquote>
          {authorName && (
            <div className="large-testimonial__quote-source">
              <Text size="caption-bold">{authorName}</Text>
              {', '}
              <Text size="caption">{authorRole}</Text>
            </div>
          )}
          <div className="large-testimonial__company-logo">{companyLogo}</div>
          {cta && <div className="large-testimonial__cta">{cta}</div>}
        </div>
      </div>

      <style jsx>
        {`
          .large-testimonial {
            &__inner {
              row-gap: 2.5rem;
              align-items: center;
            }

            &__image,
            &__header {
              grid-column: 1/-1;
            }

            &__image {
              width: 100%;
              height: 0;
              padding-bottom: 100%;
              overflow: hidden;
              border-radius: 1rem;
              position: relative;

              :global(> *) {
                width: 100%;
                height: 100%;
                position: absolute;
              }

              :global(> * > *) {
                width: 100%;
                height: 100%;
                position: absolute;
                object-fit: cover;
              }
            }

            &__header {
              text-align: center;
            }

            &__eyebrow {
              margin-bottom: 1rem;
            }

            &__quote {
              span {
                color: var(--mvp-slate);
              }

              :global(.text-size-subhead-lg > .rich-text > p) {
                color: var(--slate);
              }
            }

            &__quote-source {
              margin-top: 1.25rem;
            }

            &__company-logo {
              display: inline-flex;
              margin-top: 0.5rem;
              max-height: 1.25rem;
              :global(picture.image) {
                justify-content: center;
              }
            }

            &__cta {
              display: flex;
              flex-direction: column;
              justify-content: center;
              margin-top: 1.5rem;
            }
          }

          .mvp-background-color-slate,
          .mvp-background-color-blue,
          .mvp-background-color-deep-teal {
            .feature-spotlight__subhead {
              :global(.rich-text > ul li) {
                color: var(--paper);
              }

              :global(.rich-text > ul li span) {
                color: var(--paper);
              }
            }
          }

          @media (${mq.tablet}) {
            .large-testimonial {
              &__header {
                text-align: left;
              }

              &__image {
                grid-column: 7/-3;
                border-radius: 1.5rem;
              }

              &__eyebrow {
                margin-bottom: 1.25rem;
              }

              &__quote {
                text-indent: -0.6em;
              }

              &__company-logo {
                display: inline-flex;
                margin-top: 0.5rem;
                max-height: 1.5rem;

                :global(picture.image) {
                  justify-content: normal;
                }
              }

              &__cta {
                justify-content: start;
                flex-direction: row;
                align-items: center;
                margin-top: 2.5rem;
              }

              &--media-right {
                .large-testimonial__image {
                  grid-column: 8/-1;
                }

                .large-testimonial__header {
                  grid-column: 1/8;
                  grid-row: 1;
                }
              }

              &--media-left {
                .large-testimonial__image {
                  grid-column: 1/6;
                }

                .large-testimonial__header {
                  grid-column: 6/-1;
                }
              }
            }
          }

          @media (${mq.desktop}) {
            .large-testimonial {
              &--media-right {
                .large-testimonial__image {
                  grid-column: 7/-2;
                }

                .large-testimonial__header {
                  grid-column: 2/7;
                  padding-right: 2rem;
                }
              }

              &--media-left {
                .large-testimonial__image {
                  grid-column: 2/7;
                }

                .large-testimonial__header {
                  grid-column: 7/-2;
                  padding-left: 2rem;
                }
              }
            }
          }
        `}
      </style>
    </div>
  )
}
